import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Search,
  Link2,
  User,
  Users,
  Building2,
  Plus,
  Trash2,
  Loader,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import logger from '../../utils/logger';

const EmailLinkManager = ({ 
  isOpen, 
  onClose, 
  emailId, 
  emailSubject,
  backendUrl, 
  authToken,
  onLinkAdded 
}) => {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [links, setLinks] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ clients: [], courtiers: [], companies: [] });
  const [activeTab, setActiveTab] = useState('client');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen && emailId) {
      loadLinks();
      searchEntities(''); // Charger la liste complète à l'ouverture
    }
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults({ clients: [], courtiers: [], companies: [] });
    }
  }, [isOpen, emailId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchEntities(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/${emailId}/links`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links || []);
      }
    } catch (error) {
      logger.error('Error loading links:', error);
    }
    setLoading(false);
  };

  const searchEntities = async (query = searchQuery) => {
    setSearching(true);
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/search-entities?q=${encodeURIComponent(query)}`,
        { headers: { 'Authorization': `Bearer ${authToken}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const results = data.results || [];
        setSearchResults({
          clients: results.filter(r => r.type === 'client'),
          courtiers: results.filter(r => r.type === 'courtier'),
          companies: results.filter(r => r.type === 'company')
        });
      }
    } catch (error) {
      logger.error('Error searching entities:', error);
    }
    setSearching(false);
  };

  const addLink = async (entityType, entityId, entityName) => {
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/mail/${emailId}/add-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Lié à ${entityName}`);
        loadLinks();
        setSearchQuery('');
        setSearchResults({ clients: [], courtiers: [], companies: [] });
        if (onLinkAdded) onLinkAdded();
        
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.detail || 'Erreur lors de la liaison');
      }
    } catch (error) {
      logger.error('Error adding link:', error);
      setError('Erreur réseau');
    }
  };

  const removeLink = async (entityType, entityId) => {
    setError('');
    
    try {
      const response = await fetch(
        `${backendUrl}/api/admin/mail/${emailId}/remove-link?entity_type=${entityType}&entity_id=${entityId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        }
      );
      
      if (response.ok) {
        loadLinks();
        if (onLinkAdded) onLinkAdded();
      } else {
        const data = await response.json();
        setError(data.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      logger.error('Error removing link:', error);
      setError('Erreur réseau');
    }
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'client': return <User size={16} className="text-blue-600" />;
      case 'courtier': return <Users size={16} className="text-purple-600" />;
      case 'company': return <Building2 size={16} className="text-emerald-600" />;
      default: return <Link2 size={16} />;
    }
  };

  const getEntityLabel = (type) => {
    switch (type) {
      case 'client': return 'Client';
      case 'courtier': return 'Courtier';
      case 'company': return 'Compagnie';
      default: return type;
    }
  };

  // Filtrer les résultats pour ne pas montrer ceux déjà liés
  const filteredResults = {
    clients: searchResults.clients.filter(c => !links.some(l => l.type === 'client' && l.id === c.id)),
    courtiers: searchResults.courtiers.filter(c => !links.some(l => l.type === 'courtier' && l.id === c.id)),
    companies: searchResults.companies.filter(c => !links.some(l => l.type === 'company' && l.id === c.id))
  };

  const hasResults = filteredResults.clients.length > 0 || 
                     filteredResults.courtiers.length > 0 || 
                     filteredResults.companies.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Link2 className="text-indigo-600" size={24} />
              <h2 className="text-lg font-bold text-slate-900">Gérer les liaisons</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-1 truncate">
            {emailSubject}
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700 text-sm">
            <AlertCircle size={16} className="mr-2 flex-shrink-0" />
            {error}
          </div>
        )}
        
        {success && (
          <div className="mx-4 mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center text-emerald-700 text-sm">
            <CheckCircle size={16} className="mr-2 flex-shrink-0" />
            {success}
          </div>
        )}

        {/* Liaisons actuelles */}
        <div className="p-4 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Liaisons actuelles</h3>
          
          {loading ? (
            <div className="flex justify-center py-4">
              <Loader className="animate-spin text-slate-400" size={24} />
            </div>
          ) : links.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucune liaison</p>
          ) : (
            <div className="space-y-2">
              {links.map((link, index) => (
                <div 
                  key={`${link.type}-${link.id}-${index}`}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center space-x-2">
                    {getEntityIcon(link.type)}
                    <span className="text-sm text-slate-900">{link.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                      {getEntityLabel(link.type)}
                    </span>
                    {link.auto_linked && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Auto
                      </span>
                    )}
                  </div>
                  {!link.auto_linked && (
                    <button
                      onClick={() => removeLink(link.type, link.id)}
                      className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Supprimer la liaison"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recherche pour ajouter */}
        <div className="p-4 flex-1 overflow-y-auto">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Ajouter une liaison</h3>
          
          {/* Champ de recherche */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un client, courtier ou compagnie..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
            {searching && (
              <Loader className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" size={18} />
            )}
          </div>

          {/* Onglets */}
          <div className="flex space-x-2 mb-3">
            <button
              onClick={() => setActiveTab('client')}
              className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                activeTab === 'client' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <User size={14} className="mr-1" />
              Clients ({filteredResults.clients.length})
            </button>
            <button
              onClick={() => setActiveTab('courtier')}
              className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                activeTab === 'courtier' 
                  ? 'bg-purple-100 text-purple-700' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Users size={14} className="mr-1" />
              Courtiers ({filteredResults.courtiers.length})
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors ${
                activeTab === 'company' 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Building2 size={14} className="mr-1" />
              Compagnies ({filteredResults.companies.length})
            </button>
          </div>

          {/* Résultats */}
          {!searching && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {activeTab === 'client' && filteredResults.clients.map(client => (
                <button
                  key={client.id}
                  onClick={() => addLink('client', client.id, client.name)}
                  className="w-full flex items-center justify-between p-2 hover:bg-blue-50 rounded-lg transition-colors text-left"
                >
                  <div className="flex items-center space-x-2">
                    <User size={16} className="text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{client.name}</p>
                      {client.email && (
                        <p className="text-xs text-slate-500">{client.email}</p>
                      )}
                    </div>
                  </div>
                  <Plus size={16} className="text-blue-600" />
                </button>
              ))}

              {activeTab === 'courtier' && filteredResults.courtiers.map(courtier => (
                <button
                  key={courtier.id}
                  onClick={() => addLink('courtier', courtier.id, courtier.name)}
                  className="w-full flex items-center justify-between p-2 hover:bg-purple-50 rounded-lg transition-colors text-left"
                >
                  <div className="flex items-center space-x-2">
                    <Users size={16} className="text-purple-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{courtier.name}</p>
                      {courtier.email && (
                        <p className="text-xs text-slate-500">{courtier.email}</p>
                      )}
                    </div>
                  </div>
                  <Plus size={16} className="text-purple-600" />
                </button>
              ))}

              {activeTab === 'company' && filteredResults.companies.map(company => (
                <button
                  key={company.id}
                  onClick={() => addLink('company', company.id, company.name)}
                  className="w-full flex items-center justify-between p-2 hover:bg-emerald-50 rounded-lg transition-colors text-left"
                >
                  <div className="flex items-center space-x-2">
                    <Building2 size={16} className="text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{company.name}</p>
                    </div>
                  </div>
                  <Plus size={16} className="text-emerald-600" />
                </button>
              ))}

              {!hasResults && !searching && (
                <p className="text-sm text-slate-500 text-center py-4">
                  {searchQuery ? `Aucun résultat pour "${searchQuery}"` : 'Aucun contact trouvé'}
                </p>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default EmailLinkManager;
